import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import apiClient from "@/services/api/apiClient"
import {
  BulkDocumentTagsResponse,
  CreateKnowledgeBaseCategoryRequest,
  CreateKnowledgeBaseTagRequest,
  DocumentTagsResponse,
  KnowledgeBaseCategory,
  KnowledgeBaseTag,
  SetBulkDocumentTagsRequest,
  SetDocumentTagsRequest,
  UpdateDocumentExpiryDateRequest,
  UpdateKnowledgeBaseCategoryRequest,
  UpdateKnowledgeBaseTagRequest,
} from "@/types/knowledgebaseCategories"
import { legacySlug } from "@/utils/knowledgeBaseLegacySlug"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"

export const KNOWLEDGE_BASE_CATEGORIES_QUERY_KEY = [
  "knowledgeBaseCategories",
] as const
export const KNOWLEDGE_BASE_CATEGORY_QUERY_KEY = [
  "knowledgeBaseCategory",
] as const
export const DOCUMENT_TAGS_QUERY_KEY = ["documentTags"] as const
export const KNOWLEDGE_BASE_FILES_QUERY_KEY = ["files"] as const
export const KNOWLEDGE_BASE_FILE_QUERY_KEY = ["file"] as const

const knowledgeBaseCategoriesApi = {
  getCategories: async (
    includeInactive: boolean = false,
  ): Promise<KnowledgeBaseCategory[]> => {
    const response = await apiClient.get<KnowledgeBaseCategory[]>(
      "/kb-categories/",
      {
        params: {
          include_inactive: includeInactive,
        },
      },
    )
    return response.data
  },

  getCategory: async (categoryId: string): Promise<KnowledgeBaseCategory> => {
    if (!categoryId) {
      throw new Error("Category ID is required")
    }

    const response = await apiClient.get<KnowledgeBaseCategory>(
      `/kb-categories/${categoryId}`,
    )
    return response.data
  },

  createCategory: async (
    data: CreateKnowledgeBaseCategoryRequest,
  ): Promise<KnowledgeBaseCategory> => {
    const response = await apiClient.post<KnowledgeBaseCategory>(
      "/kb-categories/",
      { ...data, slug: legacySlug(data.displayNameEn) },
    )
    return response.data
  },

  updateCategory: async (
    categoryId: string,
    data: UpdateKnowledgeBaseCategoryRequest,
  ): Promise<KnowledgeBaseCategory> => {
    if (!categoryId) {
      throw new Error("Category ID is required")
    }

    const response = await apiClient.patch<KnowledgeBaseCategory>(
      `/kb-categories/${categoryId}`,
      data,
    )
    return response.data
  },

  deleteCategory: async (categoryId: string): Promise<void> => {
    if (!categoryId) {
      throw new Error("Category ID is required")
    }

    await apiClient.delete(`/kb-categories/${categoryId}`)
  },

  restoreCategory: async (
    categoryId: string,
  ): Promise<KnowledgeBaseCategory> => {
    if (!categoryId) {
      throw new Error("Category ID is required")
    }

    const response = await apiClient.post<KnowledgeBaseCategory>(
      `/kb-categories/${categoryId}/restore`,
    )
    return response.data
  },

  createTag: async (
    categoryId: string,
    data: CreateKnowledgeBaseTagRequest,
  ): Promise<KnowledgeBaseTag> => {
    if (!categoryId) {
      throw new Error("Category ID is required")
    }

    const response = await apiClient.post<KnowledgeBaseTag>(
      `/kb-categories/${categoryId}/tags`,
      { ...data, slug: legacySlug(data.displayNameEn) },
    )
    return response.data
  },

  updateTag: async (
    categoryId: string,
    tagId: string,
    data: UpdateKnowledgeBaseTagRequest,
  ): Promise<KnowledgeBaseTag> => {
    if (!categoryId || !tagId) {
      throw new Error("Category ID and tag ID are required")
    }

    const response = await apiClient.patch<KnowledgeBaseTag>(
      `/kb-categories/${categoryId}/tags/${tagId}`,
      data,
    )
    return response.data
  },

  deleteTag: async (categoryId: string, tagId: string): Promise<void> => {
    if (!categoryId || !tagId) {
      throw new Error("Category ID and tag ID are required")
    }

    await apiClient.delete(`/kb-categories/${categoryId}/tags/${tagId}`)
  },

  restoreTag: async (
    categoryId: string,
    tagId: string,
  ): Promise<KnowledgeBaseTag> => {
    if (!categoryId || !tagId) {
      throw new Error("Category ID and tag ID are required")
    }

    const response = await apiClient.post<KnowledgeBaseTag>(
      `/kb-categories/${categoryId}/tags/${tagId}/restore`,
    )
    return response.data
  },

  getDocumentTags: async (fileId: string): Promise<DocumentTagsResponse> => {
    if (!fileId) {
      throw new Error("File ID is required")
    }

    const response = await apiClient.get<DocumentTagsResponse>(
      `/files/${fileId}/tags`,
    )
    return response.data
  },

  setDocumentTags: async (
    fileId: string,
    data: SetDocumentTagsRequest,
  ): Promise<DocumentTagsResponse> => {
    if (!fileId) {
      throw new Error("File ID is required")
    }

    const response = await apiClient.put<DocumentTagsResponse>(
      `/files/${fileId}/tags`,
      data,
    )
    return response.data
  },

  setBulkDocumentTags: async (
    data: SetBulkDocumentTagsRequest,
  ): Promise<BulkDocumentTagsResponse> => {
    const response = await apiClient.post<BulkDocumentTagsResponse>(
      `/files/bulk-tags`,
      data,
    )
    return response.data
  },

  getBatchDocumentTags: async (
    fileIds: string[],
  ): Promise<Record<string, DocumentTagsResponse>> => {
    // Collapse N parallel GETs to one POST. Backend caps at 200 ids per
    // request (see `_BATCH_TAGS_MAX`), and KB lists aren't paginated — a
    // customer with >200 files would otherwise get a 400 on every page
    // load. Chunk client-side and merge; still O(ceil(N/200)) round-trips
    // instead of O(N), and respects the server cap without the server
    // having to raise it.
    const CHUNK = 200
    const result: Record<string, DocumentTagsResponse> = {}
    for (let i = 0; i < fileIds.length; i += CHUNK) {
      const slice = fileIds.slice(i, i + CHUNK)
      const response = await apiClient.post<
        Record<string, DocumentTagsResponse>
      >(`/files/tags/batch`, { file_ids: slice })
      Object.assign(result, response.data)
    }
    return result
  },

  approveDocumentAutoTag: async (
    fileId: string,
    tagId: string,
  ): Promise<void> => {
    if (!fileId || !tagId) {
      throw new Error("File ID and tag ID are required")
    }

    await apiClient.post(`/files/${fileId}/tags/${tagId}/approve`)
  },

  removeDocumentTag: async (fileId: string, tagId: string): Promise<void> => {
    if (!fileId || !tagId) {
      throw new Error("File ID and tag ID are required")
    }

    await apiClient.delete(`/files/${fileId}/tags/${tagId}`)
  },

  updateDocumentExpiryDate: async (
    fileId: string,
    data: UpdateDocumentExpiryDateRequest,
  ): Promise<void> => {
    if (!fileId) {
      throw new Error("File ID is required")
    }

    await apiClient.put(`/files/${fileId}/expiry-date`, data)
  },
}

const invalidateKnowledgeBaseCategories = async (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: KNOWLEDGE_BASE_CATEGORIES_QUERY_KEY,
    }),
    queryClient.invalidateQueries({
      queryKey: KNOWLEDGE_BASE_CATEGORY_QUERY_KEY,
    }),
    queryClient.invalidateQueries({
      queryKey: DOCUMENT_TAGS_QUERY_KEY,
    }),
  ])
}

export const useGetKnowledgeBaseCategories = (
  includeInactive: boolean = false,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...KNOWLEDGE_BASE_CATEGORIES_QUERY_KEY, { includeInactive }],
    queryFn: () => knowledgeBaseCategoriesApi.getCategories(includeInactive),
    enabled,
  })
}

export const useGetKnowledgeBaseCategory = (
  categoryId?: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...KNOWLEDGE_BASE_CATEGORY_QUERY_KEY, { categoryId }],
    queryFn: () => knowledgeBaseCategoriesApi.getCategory(categoryId!),
    enabled: !!categoryId && enabled,
  })
}

export const useCreateKnowledgeBaseCategory = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (data: CreateKnowledgeBaseCategoryRequest) =>
      knowledgeBaseCategoriesApi.createCategory(data),
    onSuccess: async () => {
      await invalidateKnowledgeBaseCategories(queryClient)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useUpdateKnowledgeBaseCategory = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      categoryId,
      data,
    }: {
      categoryId: string
      data: UpdateKnowledgeBaseCategoryRequest
    }) => knowledgeBaseCategoriesApi.updateCategory(categoryId, data),
    onSuccess: async () => {
      await invalidateKnowledgeBaseCategories(queryClient)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useDeleteKnowledgeBaseCategory = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (categoryId: string) =>
      knowledgeBaseCategoriesApi.deleteCategory(categoryId),
    onSuccess: async () => {
      await invalidateKnowledgeBaseCategories(queryClient)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_DELETE)
}

export const useRestoreKnowledgeBaseCategory = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (categoryId: string) =>
      knowledgeBaseCategoriesApi.restoreCategory(categoryId),
    onSuccess: async () => {
      await invalidateKnowledgeBaseCategories(queryClient)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useCreateKnowledgeBaseTag = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      categoryId,
      data,
    }: {
      categoryId: string
      data: CreateKnowledgeBaseTagRequest
    }) => knowledgeBaseCategoriesApi.createTag(categoryId, data),
    onSuccess: async () => {
      await invalidateKnowledgeBaseCategories(queryClient)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useUpdateKnowledgeBaseTag = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      categoryId,
      tagId,
      data,
    }: {
      categoryId: string
      tagId: string
      data: UpdateKnowledgeBaseTagRequest
    }) => knowledgeBaseCategoriesApi.updateTag(categoryId, tagId, data),
    onSuccess: async () => {
      await invalidateKnowledgeBaseCategories(queryClient)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useDeleteKnowledgeBaseTag = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      categoryId,
      tagId,
    }: {
      categoryId: string
      tagId: string
    }) => knowledgeBaseCategoriesApi.deleteTag(categoryId, tagId),
    onSuccess: async () => {
      await invalidateKnowledgeBaseCategories(queryClient)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_DELETE)
}

export const useRestoreKnowledgeBaseTag = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      categoryId,
      tagId,
    }: {
      categoryId: string
      tagId: string
    }) => knowledgeBaseCategoriesApi.restoreTag(categoryId, tagId),
    onSuccess: async () => {
      await invalidateKnowledgeBaseCategories(queryClient)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

// Shared staleness window used by BOTH the per-row query and the bulk
// prefetch. The row query mounts in the same render as the prefetch, and
// the QueryClient default `staleTime: 0` makes a cache seeded via
// `setQueryData` instantly stale — the row refetches immediately and the
// N+1 we were trying to kill is right back. One shared constant keeps the
// two from drifting.
const DOCUMENT_TAGS_STALE_MS = 30_000

export const useGetDocumentTags = (
  fileId?: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...DOCUMENT_TAGS_QUERY_KEY, { fileId }],
    queryFn: () => knowledgeBaseCategoriesApi.getDocumentTags(fileId!),
    enabled: !!fileId && enabled,
    refetchOnWindowFocus: false,
    staleTime: DOCUMENT_TAGS_STALE_MS,
  })
}

/**
 * Prefetch tags for a list of file IDs in ONE request and seed the per-file
 * cache. Each `useGetDocumentTags(id)` for an id in `fileIds` is then an
 * instant cache hit — the whole point of this hook. Fixes the Sentry-flagged
 * N+1 on the Knowledge Base list view (~35 GETs per page render).
 *
 * The hook itself returns nothing a parent needs to render — the row-level
 * `useGetDocumentTags` keeps rendering tags the same way. We just want its
 * query to find data already in the cache.
 */
export const usePrefetchBulkDocumentTags = (fileIds: string[]) => {
  const queryClient = useQueryClient()
  // Sort ONCE and use the sorted string for BOTH the request and the query
  // key. An earlier revision kept the raw `fileIds` as the key's last slot
  // "for exhaustive-deps lint", which defeated the stability — a reordered
  // list produced a new key and refetched for no reason. The key is now the
  // single source of truth: the queryFn parses ids out of it, so React
  // Query's exhaustive-deps lint is satisfied without the raw array.
  const stableKey = useMemo(() => [...fileIds].sort().join(","), [fileIds])

  return useQuery({
    queryKey: [...DOCUMENT_TAGS_QUERY_KEY, "batch", stableKey],
    queryFn: async () => {
      const ids = stableKey ? stableKey.split(",") : []
      if (ids.length === 0) return {}
      const map = await knowledgeBaseCategoriesApi.getBatchDocumentTags(ids)
      // Hydrate each per-file cache entry so row-level consumers skip HTTP.
      // The backend contract guarantees every input id gets a key, so this
      // never leaves a row's query in a "loading forever" state. The seeded
      // entry stays FRESH for `DOCUMENT_TAGS_STALE_MS` — see the stale-time
      // comment above `useGetDocumentTags` for why that matters here.
      for (const [fileId, tags] of Object.entries(map)) {
        queryClient.setQueryData([...DOCUMENT_TAGS_QUERY_KEY, { fileId }], tags)
      }
      return map
    },
    enabled: stableKey.length > 0,
    refetchOnWindowFocus: false,
    staleTime: DOCUMENT_TAGS_STALE_MS,
  })
}

export const useSetDocumentTags = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({
      fileId,
      data,
    }: {
      fileId: string
      data: SetDocumentTagsRequest
    }) => knowledgeBaseCategoriesApi.setDocumentTags(fileId, data),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...DOCUMENT_TAGS_QUERY_KEY, { fileId: variables.fileId }],
        }),
        queryClient.invalidateQueries({
          queryKey: KNOWLEDGE_BASE_FILES_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: [
            ...KNOWLEDGE_BASE_FILE_QUERY_KEY,
            { fileId: variables.fileId },
          ],
        }),
      ])
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useSetBulkDocumentTags = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data: SetBulkDocumentTagsRequest) =>
      knowledgeBaseCategoriesApi.setBulkDocumentTags(data),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        ...variables.document_ids.map((fileId) =>
          queryClient.invalidateQueries({
            queryKey: [...DOCUMENT_TAGS_QUERY_KEY, { fileId }],
          }),
        ),
        queryClient.invalidateQueries({
          queryKey: KNOWLEDGE_BASE_FILES_QUERY_KEY,
        }),
        ...variables.document_ids.map((fileId) =>
          queryClient.invalidateQueries({
            queryKey: [...KNOWLEDGE_BASE_FILE_QUERY_KEY, { fileId }],
          }),
        ),
      ])
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useApproveDocumentAutoTag = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({ fileId, tagId }: { fileId: string; tagId: string }) =>
      knowledgeBaseCategoriesApi.approveDocumentAutoTag(fileId, tagId),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [...DOCUMENT_TAGS_QUERY_KEY, { fileId: variables.fileId }],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useRemoveDocumentTag = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({ fileId, tagId }: { fileId: string; tagId: string }) =>
      knowledgeBaseCategoriesApi.removeDocumentTag(fileId, tagId),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...DOCUMENT_TAGS_QUERY_KEY, { fileId: variables.fileId }],
        }),
        queryClient.invalidateQueries({
          queryKey: KNOWLEDGE_BASE_FILES_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: [
            ...KNOWLEDGE_BASE_FILE_QUERY_KEY,
            { fileId: variables.fileId },
          ],
        }),
      ])
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export const useUpdateDocumentExpiryDate = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({
      fileId,
      data,
    }: {
      fileId: string
      data: UpdateDocumentExpiryDateRequest
    }) => knowledgeBaseCategoriesApi.updateDocumentExpiryDate(fileId, data),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: KNOWLEDGE_BASE_FILES_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: [
            ...KNOWLEDGE_BASE_FILE_QUERY_KEY,
            { fileId: variables.fileId },
          ],
        }),
      ])
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.KB_UPLOAD)
}

export { knowledgeBaseCategoriesApi }
